/**
 * Centralized API/network error handling.
 * Never expose raw technical messages (e.g. "timeout of 30000ms exceeded") to users.
 */

import axios from 'axios';

/** Keys used when we want the UI to show a translated message via t(key). */
export const ERROR_KEYS = {
  NETWORK: '__ERROR_NETWORK__',
  GENERIC: '__ERROR_GENERIC__',
  INVALID_CREDENTIALS: '__ERROR_INVALID_CREDENTIALS__',
} as const;

export function isNetworkOrTimeoutError(err: unknown): boolean {
  if (axios.isAxiosError(err)) {
    const code = (err as any).code;
    const msg = (err.message || '').toLowerCase();
    if (code === 'ECONNABORTED' || code === 'ERR_NETWORK') return true;
    if (msg.includes('timeout') || msg.includes('exceeded')) return true;
  }
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    if (msg.includes('timeout') || msg.includes('network') || msg.includes('exceeded')) return true;
  }
  return false;
}

const FALLBACK_EN: Record<string, string> = {
  'errors.network_issue': 'There was a network issue. Check your connection to the internet and try again.',
  'errors.generic': 'Something went wrong. Please try again.',
};

/**
 * Returns a user-facing error string. Use in catch blocks: setState({ error: getFriendlyApiErrorMessage(err, t) }).
 * If t is not provided, returns English fallbacks so technical messages are never shown.
 */
export function getFriendlyApiErrorMessage(
  err: any,
  t?: (key: string) => string
): string {
  const translate = t ?? ((key: string) => FALLBACK_EN[key] ?? key);
  if (isNetworkOrTimeoutError(err)) {
    return translate('errors.network_issue');
  }
  const serverMessage = err?.response?.data?.error?.message ?? err?.response?.data?.message;
  if (typeof serverMessage === 'string' && serverMessage.trim().length > 0) {
    return serverMessage;
  }
  return translate('errors.generic');
}

/**
 * For auth flows: returns a sentinel or server message so the UI can show the right translated string.
 * Use in Login/Register: if result is ERROR_KEYS.NETWORK show t('errors.network_issue'), etc.
 */
export function getAuthErrorMessage(err: any): string {
  if (isNetworkOrTimeoutError(err)) return ERROR_KEYS.NETWORK;
  if (err?.response?.status === 401) return ERROR_KEYS.INVALID_CREDENTIALS;
  const serverMessage = err?.response?.data?.error?.message ?? err?.response?.data?.message;
  if (typeof serverMessage === 'string' && serverMessage.trim().length > 0) return serverMessage;
  return ERROR_KEYS.GENERIC;
}
